import _ from 'lodash';
import {
  getDataWithRetry,
  mapPagedResult,
  sendWithoutBody,
} from '../fetchUtils';
import {
  buildPaginationQuery,
  getPagedMeta,
  MAX_PAGE_SIZE,
} from '../pagination';
import {
  isValidMembersResponse,
  isValidMemberResponse,
  isValidTrainingRoutineSessionsResponse,
} from '../apiValidators';
import { apiBaseUrl } from '../apiConsts';
import { isTrainingRoutineSession } from '../../utils/typeUtils';
import { getNewTrackingMemberEmailPayload } from '../../utils/emailUtils';

import type {
  EmailPayload,
  Gym,
  Member,
  TrainingRoutineSession,
  WithOptionalId,
  PagedResponse,
  PaginatedListParams,
} from '../../types';
import type { FetchResult, FetchSuccess } from '../fetchUtils';
import { getDefaultError } from '../../utils';

const createSuccessResult = <T>(data: T): FetchSuccess<T> => ({
  success: true,
  data,
  status: 200,
  durationMs: 0,
});

export const notifyUserByEmail = (
  emailPayload: EmailPayload,
): Promise<FetchResult<null>> => {
  if (!emailPayload.email) {
    return Promise.resolve({
      data: null,
      success: false,
      error: getDefaultError('cannot notify user by email, no email provided'),
    });
  }
  return sendWithoutBody(`${apiBaseUrl}notifications/notify`, {
    headers: { 'content-type': 'application/json' },
    method: 'POST',
    body: JSON.stringify(emailPayload),
  });
};

export const fetchMemberById = (
  memberId: string,
): Promise<FetchResult<Member | null>> => {
  return getDataWithRetry(
    `${apiBaseUrl}members/${memberId}`,
    {},
    isValidMemberResponse,
  );
};

export const fetchMembersByIds = async (
  memberIds: string[],
): Promise<FetchResult<Member[]>> => {
  if (!memberIds || memberIds.length === 0) {
    return createSuccessResult<Member[]>([]);
  }
  const response = await getDataWithRetry<PagedResponse<Member>>(
    `${apiBaseUrl}members/getByIds?page=0&size=${MAX_PAGE_SIZE}&sort=firstName`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify(memberIds),
    },
    isValidMembersResponse,
  );
  return mapPagedResult(response);
};

export const getGymMembers = async (
  gymId: string,
): Promise<FetchResult<Member[]>> => {
  const firstResponse = await getDataWithRetry<PagedResponse<Member>>(
    `${apiBaseUrl}members/gym/${gymId}?page=0&size=${MAX_PAGE_SIZE}&sort=firstName`,
    {},
    isValidMembersResponse,
  );
  if (!firstResponse.success) {
    return firstResponse;
  }

  const firstPageMembers = firstResponse.data.content ?? [];
  const { totalPages } = getPagedMeta(firstResponse.data);
  if (totalPages <= 1) {
    return {
      ...firstResponse,
      data: firstPageMembers,
    };
  }

  const remainingResponses = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, index) =>
      getDataWithRetry<PagedResponse<Member>>(
        `${apiBaseUrl}members/gym/${gymId}?page=${index + 1}&size=${MAX_PAGE_SIZE}&sort=firstName`,
        {},
        isValidMembersResponse,
      ),
    ),
  );
  for (const response of remainingResponses) {
    if (!response.success) {
      return response;
    }
  }

  return {
    ...firstResponse,
    data: [
      ...firstPageMembers,
      ...remainingResponses.flatMap((response) =>
        response.success ? response.data.content ?? [] : [],
      ),
    ],
  };
};

export const fetchMembersPageByGymId = (
  gymId: string,
  params: PaginatedListParams,
): Promise<FetchResult<PagedResponse<Member>>> => {
  return getDataWithRetry<PagedResponse<Member>>(
    `${apiBaseUrl}members/gym/${gymId}?${buildPaginationQuery(params)}`,
    {},
    isValidMembersResponse,
  );
};

// NOTE: Primarily used by internal analytics tooling; dashboard UI does not
// currently surface the full session history.
export const getMemberSessions = async (
  memberId: string,
): Promise<FetchResult<TrainingRoutineSession[]>> => {
  const response = await getDataWithRetry<
    PagedResponse<TrainingRoutineSession>
  >(
    `${apiBaseUrl}training-routine-sessions/member/${memberId}?page=0&size=150&sort=createdDate`,
    {},
    isValidTrainingRoutineSessionsResponse,
  );
  if (!response.success) {
    return response;
  }
  const sanitized =
    response.data.content?.filter(isTrainingRoutineSession) ?? [];
  return {
    ...response,
    data: sanitized,
  };
};

export const createNewMember = async (
  member: WithOptionalId<Member>,
  gym: Gym,
  trainerId?: string,
): Promise<FetchResult<Member | null>> => {
  const { id, ...memberData } = member;
  const createResponse = await getDataWithRetry<Member | null>(
    `${apiBaseUrl}members`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify({ ...memberData, gymIds: [gym.id] }),
    },
    isValidMemberResponse,
  );
  if (!createResponse.success || !createResponse.data) {
    return createResponse;
  }
  const createdMember = createResponse.data;
  const associateMemberToGymResult = await sendWithoutBody(
    `${apiBaseUrl}gyms/${gym.id}/associate/members`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'POST',
      body: JSON.stringify([createdMember.id]),
    },
  );
  if (!associateMemberToGymResult.success) {
    return associateMemberToGymResult;
  }
  if (trainerId) {
    const associateMemberToTrainerResult = await sendWithoutBody(
      `${apiBaseUrl}trainers/${trainerId}/associate/members`,
      {
        headers: { 'content-type': 'application/json' },
        method: 'POST',
        body: JSON.stringify([createdMember.id]),
      },
    );
    if (!associateMemberToTrainerResult.success) {
      return associateMemberToTrainerResult;
    }
  }
  const refreshedMemberResponse = await fetchMemberById(createdMember.id);
  if (!refreshedMemberResponse.success || !refreshedMemberResponse.data) {
    return refreshedMemberResponse;
  }
  const refreshedMember = refreshedMemberResponse.data;
  const notifyResult = await notifyUserByEmail(
    getNewTrackingMemberEmailPayload(createdMember, gym),
  );
  if (!notifyResult.success) {
    return notifyResult;
  }
  return {
    ...createResponse,
    data: {
      ...refreshedMember,
      gymIds: [gym.id],
    },
  };
};

const batchAssociateTrainers = async (
  memberId: string,
  trainerIds: string[],
  endpoint: 'associate' | 'unassociate',
): Promise<FetchResult<null>> => {
  if (!trainerIds || !trainerIds.length) {
    return createSuccessResult<null>(null);
  }

  const results = await Promise.all(
    trainerIds.map((trainerId) =>
      sendWithoutBody(
        `${apiBaseUrl}trainers/${trainerId}/${endpoint}/members`,
        {
          headers: { 'content-type': 'application/json' },
          method: 'POST',
          body: JSON.stringify([memberId]),
        },
      ),
    ),
  );

  const failedResult = results.find((result) => !result.success);
  if (failedResult) {
    return failedResult;
  }
  return createSuccessResult<null>(null);
};

export const updateMemberData = async (
  oldMemberData: Member,
  newMemberData: Member,
): Promise<FetchResult<Member | null>> => {
  if (!newMemberData?.id) {
    return {
      data: null,
      success: false,
      error: getDefaultError('no member id provided'),
    };
  }
  const updateResponse = await getDataWithRetry<Member | null>(
    `${apiBaseUrl}members`,
    {
      headers: { 'content-type': 'application/json' },
      method: 'PUT',
      body: JSON.stringify(newMemberData),
    },
    isValidMemberResponse,
  );
  if (!updateResponse.success || !updateResponse.data) {
    return updateResponse;
  }
  const updatedMember = updateResponse.data;

  const trainerIdsToUnAssociate = _.difference(
    oldMemberData.trainerIds,
    newMemberData.trainerIds,
  );
  const trainerIdsToAssociate = _.difference(
    newMemberData.trainerIds,
    oldMemberData.trainerIds,
  );
  const unassociateResult = await batchAssociateTrainers(
    newMemberData.id,
    trainerIdsToUnAssociate,
    'unassociate',
  );
  if (!unassociateResult.success) {
    return unassociateResult;
  }
  const associateResult = await batchAssociateTrainers(
    newMemberData.id,
    trainerIdsToAssociate,
    'associate',
  );
  if (!associateResult.success) {
    return associateResult;
  }
  return {
    ...updateResponse,
    data: {
      ...updatedMember,
      trainerIds: newMemberData.trainerIds,
    },
  };
};
